import { createColumnHelper, type Row } from '@tanstack/react-table';
import { LockIcon } from 'lucide-react';
import * as z from 'zod';

import {
  OrderCurrency,
  OrderLineManagedAction,
  OrderLineStatus,
} from '@invenco/common-interface/sales';
import { FulfillmentStrategy, SkuType } from '@invenco/products-interface';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { SkuBadge } from '@/pages/seller/components/sku-badge';
import { formatMoney, formatNumber } from '@/shared/helpers';

export type OrderLine = {
  id: string;
  lineNumber?: number | null;
  skuId?: string | null;
  skuName: string;
  externalSkuName?: string | null;
  description: string;
  qtyOrdered: number;
  unitPrice: number;
  discountRate?: number | null;
  discountAmount?: number | null;
  status?: OrderLineStatus | null;
  qtyReserved?: number | null;
  qtyCommitted?: number | null;
  qtyShipped?: number | null;
  qtyBalance?: number | null;
  assemblyOrderLineId?: string | null;
  assemblyComponentQty?: number | null;
  managedActions?: OrderLineManagedAction[] | null;
  fulfillmentStrategy?: FulfillmentStrategy | null;
};

export type OrderLineRow = Omit<OrderLine, 'qtyOrdered' | 'unitPrice'> & {
  qtyOrdered?: number;
  unitPrice?: number;
  discount: string;
  lineTotal: number;
  components?: OrderLineRow[];
};

export type OrderLineAddInput = Pick<
  OrderLine,
  'skuName' | 'description' | 'qtyOrdered' | 'unitPrice'
> & {
  discountRate: number;
  discountAmount: number;
};

export type OrderLineUpdatePatch = Partial<
  Pick<
    OrderLineAddInput,
    'skuName' | 'description' | 'qtyOrdered' | 'unitPrice' | 'discountRate' | 'discountAmount'
  >
>;

const columnHelper = createColumnHelper<OrderLineRow>();

export const orderLineEditableSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  qtyOrdered: z.number('Ordered quantity is required').int().nonnegative(),
  unitPrice: z.number('Unit cost is required').nonnegative(),
  discount: z.string().regex(/^[0-9]*(\.[0-9]{0,2})?%?$/, 'Use a number or percentage'),
});

export type OrderLineEditableValues = z.output<typeof orderLineEditableSchema>;

type GetOrderLineColumnsOptions = {
  orderCurrency?: OrderCurrency;
  reserveTypeIconSpace?: boolean;
};

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function getDiscountFields(
  discount: string | null | undefined,
): Pick<OrderLineAddInput, 'discountRate' | 'discountAmount'> {
  const formattedDiscount = discount?.trim() ?? '';
  if (formattedDiscount.endsWith('%')) {
    return { discountRate: Number(formattedDiscount.slice(0, -1)) || 0, discountAmount: 0 };
  }

  return { discountRate: 0, discountAmount: Number(formattedDiscount) || 0 };
}

export function getOrderLineDiscountInputValue({
  discountRate,
  discountAmount,
}: Pick<OrderLine, 'discountRate' | 'discountAmount'>) {
  if (discountRate && discountRate > 0) return `${discountRate}%`;
  return discountAmount?.toString() ?? '0%';
}

export function getOrderLineDiscountAmount(line: Partial<OrderLineRow>) {
  const discountFields = line.discount !== undefined ? getDiscountFields(line.discount) : line;
  const qtyOrdered = line.qtyOrdered ?? 0;
  const unitPrice = line.unitPrice ?? 0;

  if (qtyOrdered <= 0) return 0;
  if (discountFields.discountRate && discountFields.discountRate > 0) {
    return qtyOrdered * unitPrice * (discountFields.discountRate / 100);
  }

  return discountFields.discountAmount ?? 0;
}

export function getOrderLineTotal(line: Partial<OrderLineRow>) {
  const qtyOrdered = line.qtyOrdered ?? 0;
  if (qtyOrdered <= 0) return 0;

  return roundMoney(qtyOrdered * (line.unitPrice ?? 0) - getOrderLineDiscountAmount(line));
}

function formatOptionalMoney(value: number | null | undefined, currency = OrderCurrency.AUD) {
  if (value === null || value === undefined) return undefined;
  return formatMoney(value, currency);
}

function formatOptionalNumber(value: number | null | undefined, precision = 0) {
  if (value === null || value === undefined) return undefined;
  return formatNumber(value, precision);
}

function formatDiscountValue(value: string | null | undefined, currency = OrderCurrency.AUD) {
  const discount = value ?? '0%';
  if (discount.endsWith('%')) {
    return `${formatNumber(Number(discount.slice(0, -1)) || 0, 2)}%`;
  }

  return formatMoney(Number(discount) || 0, currency);
}

export function normalizeOrderLine(line: OrderLine): OrderLineRow {
  const discount = getOrderLineDiscountInputValue(line);
  return {
    ...line,
    discount,
    lineTotal: getOrderLineTotal({ ...line, discount }),
  };
}

export function isOrderLineManaged(line: Pick<OrderLineRow, 'managedActions'>) {
  return line.managedActions?.includes(OrderLineManagedAction.EDIT) ?? false;
}

function BlankOrderLineCell() {
  return <span className="invisible" aria-hidden />;
}

function isBundleComponentLine(line: Pick<OrderLineRow, 'assemblyOrderLineId'>) {
  return Boolean(line.assemblyOrderLineId);
}

function isLastSubRow(row: Row<OrderLineRow>) {
  const subRows = row.getParentRow()?.subRows;
  return subRows?.[subRows.length - 1]?.id === row.id;
}

// viewBox is 20×40: the stem sits at x=10 and the branch curves out to the SKU
// content (x=18) at the row's vertical mid-point (y≈16–20).
const BUNDLE_CONNECTOR_ELBOW_PATH = 'M10 0 V16 C10 18 12 20 18 20'; // last component (└)
const BUNDLE_CONNECTOR_TEE_PATH = 'M10 0 V40 M10 16 C10 18 12 20 18 20'; // mid component (├)

function BundleComponentConnector({ isLast }: { isLast: boolean }) {
  return (
    <span className="absolute top-0 left-4 h-full w-5 text-border" aria-hidden>
      <svg className="absolute inset-0 size-full overflow-visible" viewBox="0 0 20 40" fill="none">
        <path
          d={isLast ? BUNDLE_CONNECTOR_ELBOW_PATH : BUNDLE_CONNECTOR_TEE_PATH}
          stroke="currentColor"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function BundleComponentSkuDescription({
  line,
  isLastComponent,
}: {
  line: OrderLineRow;
  isLastComponent: boolean;
}) {
  const componentQty = line.assemblyComponentQty;

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="sr-only">Bundle component</span>
      <BundleComponentConnector isLast={isLastComponent} />
      <span className="w-5 shrink-0" aria-hidden />
      {componentQty !== null && componentQty !== undefined ? (
        <span className="w-7 shrink-0 text-right">{componentQty} x</span>
      ) : null}
      <SkuBadge externalSkuName={line.externalSkuName} name={line.skuName} />
      <span className="truncate">{line.description}</span>
    </div>
  );
}

export function getOrderLineColumns({
  orderCurrency = OrderCurrency.AUD,
  reserveTypeIconSpace,
}: GetOrderLineColumnsOptions = {}) {
  return [
    columnHelper.accessor('description', {
      header: 'SKU / description',
      cell: ({ getValue, row }) => {
        if (isBundleComponentLine(row.original)) {
          return (
            <BundleComponentSkuDescription
              line={row.original}
              isLastComponent={isLastSubRow(row)}
            />
          );
        }

        return (
          <div className="flex min-w-0 items-center gap-2">
            {isOrderLineManaged(row.original) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label="Managed by Shopify"
                    className="inline-flex size-4 shrink-0 items-center justify-center text-primary"
                  >
                    <LockIcon className="size-3.5" aria-hidden />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Managed by Shopify</TooltipContent>
              </Tooltip>
            )}
            <SkuBadge
              externalSkuName={row.original.externalSkuName}
              name={row.original.skuName}
              type={
                row.original.fulfillmentStrategy === FulfillmentStrategy.BUNDLE
                  ? SkuType.BUNDLE
                  : undefined
              }
              reserveTypeIconSpace={reserveTypeIconSpace}
            />
            <span className="truncate">{getValue()}</span>
          </div>
        );
      },
      meta: {
        truncate: true,
        cellClassName: (cell) =>
          isBundleComponentLine(cell.row.original) ? 'relative' : undefined,
        editable: {
          label: 'Description',
          editor: {
            type: 'text',
          },
        },
      },
    }),
    columnHelper.accessor('qtyOrdered', {
      header: 'Qty',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatOptionalNumber(getValue(), 0)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Qty',
          editor: {
            type: 'number',
            min: 0,
            step: 1,
          },
        },
      },
    }),
    columnHelper.accessor('unitPrice', {
      header: 'Unit cost',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatOptionalMoney(getValue(), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Unit cost',
          editor: {
            type: 'number',
            min: 0,
            step: 'any',
          },
        },
      },
    }),
    columnHelper.accessor('discount', {
      header: 'Discount',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatDiscountValue(getValue(), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Discount',
          editor: {
            type: 'text',
          },
        },
      },
    }),
    columnHelper.accessor('lineTotal', {
      header: 'Line total',
      cell: ({ row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatMoney(getOrderLineTotal(row.original), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          renderFromDraft: (draft) => formatMoney(getOrderLineTotal(draft), orderCurrency),
        },
      },
    }),
  ];
}
